import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Shield } from "lucide-react";
import api from "../lib/api";
import { DEFAULT_PROJECTS_PAGE } from "../lib/homeContent";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";
import ProjectCard from "../components/ProjectCard";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";

export default function Projects() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [c, setC] = useState(DEFAULT_PROJECTS_PAGE);

  useEffect(() => {
    api
      .get("/projects")
      .then(({ data }) => setProjects(data))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
    api.get("/content/projects").then(({ data }) => setC({ ...DEFAULT_PROJECTS_PAGE, ...data })).catch(() => {});
  }, []);

  const funded = (selected?.status || "").toLowerCase() === "funded";

  return (
    <div className="min-h-screen bg-sp-dark text-white sp-grain" data-testid="projects-page">
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-sp-dark/80 border-b border-white/10">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 h-20 flex items-center justify-between">
          <Logo variant="dark" />
          <nav className="hidden md:flex items-center gap-7">
            <Link to="/" data-testid="projects-nav-home" className="font-sans text-sm text-white/70 hover:text-white transition-colors duration-300">Home</Link>
            <Link to="/who-we-are" data-testid="projects-nav-who" className="font-sans text-sm text-white/70 hover:text-white transition-colors duration-300">Who We Are</Link>
            <Link to="/directors" data-testid="projects-nav-directors" className="font-sans text-sm text-white/70 hover:text-white transition-colors duration-300">Directors</Link>
            <Link to="/connect" data-testid="projects-nav-connect" className="font-sans text-sm text-white/70 hover:text-white transition-colors duration-300">Connect</Link>
          </nav>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline font-mono text-xs uppercase tracking-[0.16em] text-white/50">
              {user?.name}
            </span>
            {user?.role === "admin" && (
              <button
                data-testid="projects-admin-btn"
                onClick={() => navigate("/admin")}
                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-xs text-white/80 hover:bg-white/5 transition-colors duration-300"
              >
                <Shield className="w-3.5 h-3.5" /> Console
              </button>
            )}
            <button
              data-testid="projects-logout-btn"
              onClick={() => { logout(); navigate("/"); }}
              className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-xs text-white hover:bg-white/20 transition-colors duration-300"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-7xl px-6 lg:px-10 pt-16 pb-8">
        <p className="sp-eyebrow text-sky-300/80">{c.eyebrow}</p>
        <h1 className="mt-6 font-serif text-4xl md:text-6xl text-white leading-[1.08] max-w-3xl">
          {c.title}
        </h1>
        <p className="mt-6 font-sans text-lg text-white/60 max-w-2xl">
          {c.subtitle}
        </p>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10 pb-32">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-96 rounded-sm bg-white/5 animate-pulse" />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <p className="text-white/50 font-sans" data-testid="projects-empty">No projects available yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((p, i) => (
              <ProjectCard key={p.id} project={p} index={i} onClick={() => setSelected(p)} />
            ))}
          </div>
        )}
      </section>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-2xl bg-sp-darksurface border-white/10 text-white" data-testid="project-detail-dialog">
          {selected && (
            <>
              <div className="relative -mx-6 -mt-6 mb-4 h-56 overflow-hidden">
                {selected.image_url && (
                  <img src={selected.image_url} alt={selected.title} className="h-full w-full object-cover opacity-80" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-sp-darksurface to-transparent" />
                {funded && (
                  <span
                    className="absolute inset-0 m-auto h-fit w-fit select-none font-serif text-4xl uppercase tracking-[0.25em] text-white/15 border-y-2 border-white/15 px-6 py-2"
                    style={{ transform: "rotate(-14deg)" }}
                    data-testid="dialog-funded-watermark"
                  >
                    Funded
                  </span>
                )}
              </div>
              <DialogHeader>
                <p className="font-mono text-xs uppercase tracking-[0.18em] text-sky-300/70">
                  {selected.category} · {selected.location}
                </p>
                <DialogTitle className="font-serif text-3xl text-white">{selected.title}</DialogTitle>
                <DialogDescription className="sr-only">Project brief for {selected.title}</DialogDescription>
              </DialogHeader>
              <p className="font-sans text-base leading-relaxed text-white/70">
                {selected.description || selected.summary}
              </p>
              <div className="mt-4 flex items-center gap-8 border-t border-white/10 pt-5">
                <div>
                  <p className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-white/40">Capital</p>
                  <p className="font-serif text-2xl text-white">{selected.capital || "—"}</p>
                </div>
                <div>
                  <p className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-white/40">Status</p>
                  <p className="font-sans text-base font-medium text-white capitalize">{selected.status}</p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
